import React, { useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { showCustomToast } from "../CustomToast";

interface MCPModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MCPModal = ({ isOpen, onClose }: MCPModalProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose]);

  const handleCopy = (text: string, message: string) => {
    navigator.clipboard.writeText(text);
    showCustomToast(message, "check");
  };

  const steps = [
    {
      title: (
        <>
          Install{" "}
          <a
            target="_blank"
            href="https://code.claude.com/docs/en/ide-integrations"
            className="underline decoration-gray-900"
          >
            Claude Code in your IDE
          </a>
        </>
      ),
      description: null,
      content: null,
    },
    {
      title: "Run this command in your terminal in your project",
      description: null,
      content: (
        <div className="mt-4 flex items-center justify-between gap-4 bg-white/[0.08] py-3 border-white/[0.08] border-l-[4px] px-4">
          <div className="geist text-[14px] leading-[20px] flex-1 break-words">
            <span className="text-[#569CD6]">claude</span>{" "}
            <span className="text-[#CE9178]">mcp</span>{" "}
            <span className="text-[#CE9178]">add</span>{" "}
            <span className="text-[#CE9178]">Toolbar</span>{" "}
            <span className="text-[#CE9178]">https://mcp.usetool.bar/mcp</span>{" "}
            <span className="text-[#CE9178]">-t</span>{" "}
            <span className="text-[#CE9178]">http</span>{" "}
            <span className="text-[#CE9178]">-s</span>{" "}
            <span className="text-[#CE9178]">user</span>
          </div>
          <button
            onClick={() =>
              handleCopy(
                "claude mcp add Toolbar https://mcp.usetool.bar/mcp -t http -s user",
                "Command copied",
              )
            }
            className="geist transition-all text-[14px] leading-[20px] font-medium text-white/[0.56] hover:text-white flex-shrink-0"
          >
            COPY
          </button>
        </div>
      ),
    },
    {
      title: "How to use it: Paste your bug report link to Claude",
      description:
        "Copy any Toolbar report link and paste it into Claude Code to get AI-powered fixes",
      content: null,
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="p-6 flex flex-col rounded-[24px] w-[480px] bg-gray-200 max-h-[90vh] overflow-y-auto mx-4"
          >
            <div>
              <p className="text-[20px] leading-[28px] font-medium text-white mb-6">
                Configure Toolbar MCP for Claude Code
              </p>

              <div className="flex flex-col gap-6">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-6 relative">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-[32px] h-[32px] rounded-[10px] bg-gray-400 border-[1px] border-gray-500 flex items-center justify-center font-semibold text-[16px] leading-[24px]">
                        {index + 1}
                      </div>
                      {index < steps.length - 1 && (
                        <div className="w-[1px] bg-white/[0.16] absolute top-[32px] bottom-[-24px]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <h2
                        className={`text-[16px] leading-[24px] font-semibold text-gray-900 ${step.description || step.content ? "mb-1" : ""}`}
                      >
                        {step.title}
                      </h2>
                      {step.description && (
                        <p className="text-[14px] leading-[20px] text-white/[0.56] font-medium">
                          {step.description}
                        </p>
                      )}
                      {step.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MCPModal;
